import Konva from 'konva';
import { FieldTransform } from '../components/FieldCanvas';

/** Convert stage-space coordinates (zoom/pan invariant) to game-space feet. */
export function stageCoordsToField(
  stageX: number,
  stageY: number,
  t: FieldTransform,
): { xFt: number; zFt: number } {
  if (t.orientation === 'horizontal') {
    // canvas-X = game-Z axis, canvas-Y = game-X axis
    return {
      zFt: (stageX - t.fieldOriginX) / t.basePxPerFoot - 75,
      xFt: (stageY - t.fieldOriginY) / t.basePxPerFoot - 60,
    };
  } else {
    // canvas-X = game-X axis, canvas-Y = game-Z axis
    return {
      xFt: (stageX - t.fieldOriginX) / t.basePxPerFoot - 60,
      zFt: (stageY - t.fieldOriginY) / t.basePxPerFoot - 75,
    };
  }
}

/** Convert game-space feet to stage-space coordinates. */
export function fieldToStageCoords(
  xFt: number,
  zFt: number,
  t: FieldTransform,
): { x: number; y: number } {
  if (t.orientation === 'horizontal') {
    return {
      x: t.fieldOriginX + (zFt + 75) * t.basePxPerFoot,
      y: t.fieldOriginY + (xFt + 60) * t.basePxPerFoot,
    };
  } else {
    return {
      x: t.fieldOriginX + (xFt + 60) * t.basePxPerFoot,
      y: t.fieldOriginY + (zFt + 75) * t.basePxPerFoot,
    };
  }
}

/**
 * Mirror rotation across the field's centerline (Z=0) for the given orientation.
 *
 * Horizontal: centerline is a vertical canvas line (canvas-X = game-Z).
 *   Reflecting across it negates the canvas-X component of the direction vector.
 *   Result: (360 - rot) % 360
 *
 * Vertical: centerline is a horizontal canvas line (canvas-Y = game-Z).
 *   Reflecting across it negates the canvas-Y component.
 *   Result: (180 - rot + 360) % 360
 */
export function mirrorRotation(rotDeg: number, orientation: 'horizontal' | 'vertical'): number {
  return orientation === 'horizontal'
    ? (360 - rotDeg) % 360
    : (180 - rotDeg + 360) % 360;
}

/** Snap a value in feet to the nearest grid increment. */
export function snapToGrid(ft: number, gridFt: number): number {
  return Math.round(ft / gridFt) * gridFt;
}

/** Clamp game-space feet to field bounds. */
export function clampField(xFt: number, zFt: number): { xFt: number; zFt: number } {
  return {
    xFt: Math.max(-60, Math.min(60, xFt)),
    zFt: Math.max(-75, Math.min(75, zFt)),
  };
}

/** Convert screen-space pointer to stage-space coordinates (accounts for zoom/pan). */
export function pointerToStageCoords(
  stage: Konva.Stage,
): { x: number; y: number } | null {
  const pos = stage.getPointerPosition();
  if (!pos) return null;
  return {
    x: (pos.x - stage.x()) / stage.scaleX(),
    y: (pos.y - stage.y()) / stage.scaleY(),
  };
}
